/**
 * WaterQualityCard — daily water test log (pH, hardness, alkalinity, chlorine,
 * iron, TDS). Date is always "today". Per-parameter history with trend chart
 * and CSV export.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Droplets, Plus, Trash2, Download, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WATER_PARAMS = [
  { key: "ph", label: "pH", unit: "", hint: "ideal 6.5–8" },
  { key: "hardness", label: "Hardness", unit: "ppm", hint: "< 60 ppm soft" },
  { key: "alkalinity", label: "Alkalinity", unit: "ppm", hint: "" },
  { key: "chlorine", label: "Chlorine", unit: "ppm", hint: "" },
  { key: "iron", label: "Iron", unit: "ppm", hint: "< 0.1 ideal" },
  { key: "tds", label: "TDS", unit: "ppm", hint: "" },
];

const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });

export default function WaterQualityCard() {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [param, setParam] = useState("ph");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/maintenance/water-tests`, { headers: { "x-no-cache": "1" } });
      setRecords(res.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const vals = {};
    WATER_PARAMS.forEach(({ key }) => { if (form[key] !== undefined && form[key] !== "") vals[key] = Number(form[key]); });
    if (Object.keys(vals).length === 0) { toast.error("Enter at least one measurement"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/water-tests`, { ...vals, note: (form.note || "").trim() || null });
      toast.success("Water test recorded");
      setForm({});
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the water test from ${fmtDate(r.date)}?`)) return;
    try {
      await axios.delete(`${API}/maintenance/water-tests/${r.id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sel = WATER_PARAMS.find((p) => p.key === param);
  const history = records.filter((r) => r[param] != null);
  const chartData = [...history].reverse().map((r) => ({ date: r.date.slice(5), value: r[param] }));

  const exportCsv = () => {
    const rows = [["Date", `${sel.label}${sel.unit ? ` (${sel.unit})` : ""}`, "By", "Note"]];
    history.forEach((r) => rows.push([r.date, r[param], r.by || "", r.note || ""]));
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `water-${param}-history.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="border rounded-xl bg-white" data-testid="water-quality-card">
      <button onClick={() => setOpen(!open)} data-testid="water-quality-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 rounded-xl">
        <span className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
          <Droplets className="w-4 h-4 text-sky-600" />
          Water quality tests
          <span className="text-xs font-normal text-muted-foreground">({records.length} records)</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">New tests are logged with today's date automatically.</p>
            <Button size="sm" className="bg-brand-navy text-white h-8" onClick={() => setFormOpen(!formOpen)} data-testid="add-water-test-btn">
              <Plus className="w-4 h-4 mr-1" /> Add test
            </Button>
          </div>

          {formOpen && (
            <div className="border rounded-xl p-3 bg-sky-50/40 border-sky-100 space-y-2" data-testid="water-test-form">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {WATER_PARAMS.map(({ key, label, unit, hint }) => (
                  <div key={key}>
                    <Label className="text-xs">{label}{unit ? ` (${unit})` : ""}</Label>
                    <Input type="number" step="any" className="bg-white h-8" value={form[key] ?? ""}
                      placeholder={hint} data-testid={`water-input-${key}`}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Note (optional)</Label>
                  <Input className="bg-white h-8" value={form.note ?? ""} data-testid="water-note-input"
                    onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={save} disabled={busy} data-testid="save-water-test-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            </div>
          )}

          {/* Parameter selector */}
          <div className="flex gap-1.5 flex-wrap">
            {WATER_PARAMS.map(({ key, label }) => {
              const latest = records.find((r) => r[key] != null);
              return (
                <button key={key} onClick={() => setParam(key)} data-testid={`water-param-${key}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                    param === key ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-neutral-600 hover:bg-neutral-50"
                  }`}>
                  {label}{latest ? ` · ${latest[key]}` : ""}
                </button>
              );
            })}
          </div>

          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">No {sel.label} readings yet.</p>
          ) : (
            <>
              {chartData.length > 1 && (
                <div className="h-36 border rounded-lg p-2" data-testid="water-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#0175cc" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{sel.label} history ({history.length})</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportCsv} data-testid="export-water-csv-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                </Button>
              </div>
              <div className="max-h-[220px] overflow-y-auto border rounded-lg divide-y">
                {history.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`water-row-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm"><span className="font-semibold">{r[param]}{sel.unit ? ` ${sel.unit}` : ""}</span>
                        <span className="text-xs text-muted-foreground"> · {fmtDate(r.date)}{r.by ? ` · ${r.by}` : ""}</span></p>
                      {r.note && <p className="text-xs text-neutral-500 truncate">{r.note}</p>}
                    </div>
                    <button onClick={() => remove(r)} className="text-red-400 hover:text-red-600 shrink-0" data-testid={`delete-water-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
